import { Directive, Input } from '@angular/core';
import { AuthenticationService, System, UserPermissions, resolveRTMonitoringInfo } from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest, interval, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { SystemsState } from '../../store';
import {
  activateRealTimeTelemetry,
  deactivateRealTimeTelemetry,
  setRealTimeTelemetryOverlayHidden,
} from '../../store/actions/real-time.actions';
import { selectRealTimeTelemetrySessionBySystemId } from '../../store/selectors/real-time.selectors';
import { selectRealTimeTelemetryAvailability } from '../../store/selectors/system.selectors';
import { IReduxBlock } from '../../store/helpers/decorator';

interface RTSessionTimingInput {
  active?: boolean;
  duration?: number;
  endedAt?: number;
  startTS?: number | null;
  stopping?: boolean;
}

interface RTSessionTimingState {
  countdownSeconds: number | null;
  isStopping: boolean;
  rtProgress: number;
}

/**
 * Abstract base for RT components (console & overlay).
 *
 * Houses the shared observables (`rtTick$`, `systemUid$`, `rtSession$`,
 * `canUseRealTime$`, `userCanUseRealTime$`) and the `onStart()`/`onStop()`
 * dispatch logic, so concrete subclasses only define their own view-state
 * and lifecycle behaviour.
 */
@Directive()
export abstract class RtBaseComponent implements IReduxBlock {
  // --- IReduxBlock contract (populated by @CreateBlockReduxSupport) ---
  abstract readonly _instanceId$: Observable<string>;
  abstract refresh$: Subject<void>;
  abstract _refresh$: Observable<boolean>;

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly rtTick$ = interval(1000).pipe(startWith(0));

  readonly systemUid$ = this.system$.pipe(
    map((system) => system?.uid ?? null),
    distinctUntilChanged(),
  );

  readonly rtSession$ = this.systemUid$.pipe(
    switchMap((systemId) => {
      if (!systemId) {
        return of({
          active: false,
          duration: 0,
          endedAt: undefined as number | undefined,
          lastTelemetryReceivedAt: undefined as number | undefined,
          overlayHidden: false,
          overlayRequestedByUser: false,
          startTS: null as number | null,
          stopping: false,
        });
      }

      return this.store.select(selectRealTimeTelemetrySessionBySystemId(systemId));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly rtAvailability$ = this.store.select(selectRealTimeTelemetryAvailability).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly canUseRealTime$ = this.rtAvailability$.pipe(
    map((availability) => Boolean(availability.canUseRealTime)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly userCanUseRealTime$ = combineLatest([
    this.system$,
    this.authenticationService.user$,
  ]).pipe(
    map(([system, loggedUser]) => {
      const user = system?.aws?.users?.find((u) => u.email === loggedUser?.email);
      if (!user) {
        return false;
      }

      return Boolean(user) && (user.userLevel === UserPermissions.hidden ||
        user.userLevel === UserPermissions.Owner ||
        user.userLevel === UserPermissions.Admin);
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    readonly store: Store<SystemsState>,
    protected readonly authenticationService: AuthenticationService,
  ) {}

  protected getRTSessionTiming(
    session: RTSessionTimingInput,
    now = Date.now(),
  ): RTSessionTimingState {
    const totalSeconds = Math.max(1, Number(session.duration || 0));
    const endTS = session.startTS ? session.startTS + totalSeconds * 1000 : null;
    const rawCountdownSeconds = session.active && endTS !== null
      ? Math.round((endTS - now) / 1000)
      : null;
    const countdownSeconds = rawCountdownSeconds !== null
      ? Math.max(0, rawCountdownSeconds)
      : null;
    const elapsedSeconds = countdownSeconds === null
      ? totalSeconds
      : Math.max(0, totalSeconds - countdownSeconds);

    return {
      countdownSeconds,
      isStopping: Boolean(
        session.active && (session.stopping || (rawCountdownSeconds !== null && rawCountdownSeconds <= 0)),
      ),
      rtProgress: session.active
        ? Math.min(100, Math.round((elapsedSeconds / totalSeconds) * 100))
        : (session.endedAt ? 100 : 0),
    };
  }

  onStop(): void {
    const system = this.system$.value;
    const rtInfo = resolveRTMonitoringInfo(system);
    const systemId = system?.uid;
    if (!systemId || !rtInfo) {
      return;
    }

    this.store.dispatch(deactivateRealTimeTelemetry({
      plantId: systemId,
      systemId,
      emsId: rtInfo.emsId,
      monitoringType: rtInfo.monitoringType,
    }));
  }

  onStart(): void {
    const system = this.system$.value;
    const rtInfo = resolveRTMonitoringInfo(system);
    const systemId = system?.uid;
    if (!systemId || !rtInfo) {
      return;
    }

    this.store.dispatch(setRealTimeTelemetryOverlayHidden({ systemId, hidden: false }));
    this.store.dispatch(activateRealTimeTelemetry({
      plantId: systemId,
      systemId,
      emsId: rtInfo.emsId,
      monitoringType: rtInfo.monitoringType,
    }));
  }
}
